import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { 
  ArrowLeft, 
  ArrowRight,
  Calendar, 
  User, 
  Clock, 
  Tag,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  ChevronRight,
  Eye,
  MessageSquare
} from 'lucide-react';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const response = await blogsAPI.getBySlug(slug);
      if (response.data.success) {
        setBlog(response.data.data);
        // Fetch related blogs
        fetchRelatedBlogs(response.data.data.category, response.data.data._id);
      } else {
        toast.error('Blog not found');
        navigate('/blogs');
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Failed to load blog');
      navigate('/blogs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedBlogs = async (category, currentBlogId) => {
    try {
      const response = await blogsAPI.getAll({ 
        category, 
        limit: 3
      });
      if (response.data.success) {
        setRelatedBlogs(response.data.data.filter(b => b._id !== currentBlogId).slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching related blogs:', error);
    }
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = blog?.title || 'Check out this article';
    
    switch (platform) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          toast.success('Link copied to clipboard!');
        } catch (error) {
          toast.error('Failed to copy link');
        }
        break;
      
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      
      default:
        break;
    }
    
    setShareMenuOpen(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content?.split(' ').length || 0;
    return Math.ceil(words / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-red-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 font-medium transition-colors duration-300">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">Article Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4 transition-colors duration-300">The article you're looking for doesn't exist.</p>
          <Link 
            to="/blogs" 
            className="inline-flex items-center px-6 py-3 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Navigation Breadcrumb */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 header-spacer py-2">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 transition-colors duration-300" />
            <Link to="/blogs" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300">Blog</Link>
            <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 transition-colors duration-300" />
            <span className="text-gray-900 dark:text-white font-medium transition-colors duration-300">{blog.title}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link 
          to="/blogs"
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-8 transition-colors duration-300"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Articles
        </Link>

        {/* Article Header */}
        <article className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300">
          {/* Featured Image */}
          {blog.featuredImage?.url && (
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={blog.featuredImage.url}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="p-8 lg:p-12">
            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 transition-colors duration-300">
                <Tag className="w-3 h-3 mr-1" />
                {blog.category?.replace('-', ' ') || 'General'}
              </span>
              
              <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm transition-colors duration-300">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDate(blog.publishedAt)}
              </div>
              
              <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm transition-colors duration-300">
                <User className="w-4 h-4 mr-1" />
                {blog.author?.name || 'Admin'}
              </div>
              
              <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm transition-colors duration-300">
                <Clock className="w-4 h-4 mr-1" />
                {estimateReadingTime(blog.content)} min read
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6 leading-tight transition-colors duration-300">
              {blog.title}
            </h1>

            {/* Excerpt */}
            {blog.excerpt && (
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed font-medium transition-colors duration-300">
                {blog.excerpt}
              </p>
            )}

            {/* Share Button */}
            <div className="flex items-center justify-between mb-8 pb-8 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm transition-colors duration-300">
                  <Eye className="w-4 h-4 mr-1" />
                  {blog.views || 0} views
                </div>
              </div>
              
              <div className="relative">
                <button
                  onClick={() => setShareMenuOpen(!shareMenuOpen)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-300"
                >
                  <Share2 className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Share</span>
                </button>
                
                {shareMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10 transition-colors duration-300">
                    <div className="py-2">
                      <button
                        onClick={() => handleShare('copy')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300"
                      >
                        <Copy className="w-4 h-4 mr-3" />
                        Copy Link
                      </button>
                      <button
                        onClick={() => handleShare('facebook')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300"
                      >
                        <Facebook className="w-4 h-4 mr-3" />
                        Facebook
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300"
                      >
                        <Twitter className="w-4 h-4 mr-3" />
                        Twitter
                      </button>
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300"
                      >
                        <Linkedin className="w-4 h-4 mr-3" />
                        LinkedIn
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Article Content */}
            <div className="prose max-w-none text-gray-700 dark:text-gray-300 leading-relaxed transition-colors duration-300">
              {blog.content.split('\n').map((paragraph, index) => (
                paragraph.trim() ? (
                  <p key={index} className="mb-4">
                    {paragraph}
                  </p>
                ) : null
              ))}
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedBlogs.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 transition-colors duration-300">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedBlogs.map((relatedBlog) => (
                <Link
                  key={relatedBlog._id}
                  to={`/blogs/${relatedBlog.slug}`}
                  className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={relatedBlog.featuredImage?.url || '/images/placeholder-blog.jpg'}
                      alt={relatedBlog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm mb-2 transition-colors duration-300">
                      <Calendar className="w-3 h-3 mr-1" />
                      {formatDate(relatedBlog.publishedAt)}
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-300">
                      {relatedBlog.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mt-2 line-clamp-2 transition-colors duration-300">
                      {relatedBlog.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 text-center transition-colors duration-300">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">Stay Updated</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6 transition-colors duration-300">
            Get the latest insights and updates from Happy Steels delivered to your inbox.
          </p>
          <Link
            to="/blogs"
            className="inline-flex items-center px-6 py-3 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors duration-300 font-medium"
          >
            View More Articles
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>

      {/* Click outside to close share menu */}
      {shareMenuOpen && (
        <div 
          className="fixed inset-0 z-5"
          onClick={() => setShareMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default BlogDetail;
